import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, FileDown, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useExitIntent } from "@/hooks/useExitIntent";
import { track } from "@/lib/analytics";
import { trackCompleteRegistration } from "@/lib/leadTracking";
import {
  LeadFormSchema,
  validateLeadForm,
  validateField,
  type LeadFormErrors,
  type LeadFormField,
} from "./leadFormValidation";

export default function ExitIntentModal() {
  const location = useLocation();
  const isThankYou = location.pathname.includes("thank-you");
  const isFormStarted = typeof window !== "undefined" && sessionStorage.getItem("checkup_form_started") === "1";

  const { open, setOpen } = useExitIntent({ enabled: !isThankYou && !isFormStarted });

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<LeadFormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<LeadFormField, boolean>>>({});
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [leadStatus, setLeadStatus] = useState<"ok" | "failed">("ok");
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const downloadLinkRef = useRef<HTMLAnchorElement | null>(null);

  const isValid = useMemo(
    () => LeadFormSchema.safeParse({ first_name: firstName, email, phone }).success,
    [firstName, email, phone],
  );

  const handleBlur = (field: LeadFormField, value: string) => {
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };
  const handleChange = (field: LeadFormField, value: string, setter: (v: string) => void) => {
    setter(value);
    if (attempted || touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  useEffect(() => {
    return () => { if (downloadUrl) URL.revokeObjectURL(downloadUrl); };
  }, [downloadUrl]);

  // Track when the exit-intent modal actually fires.
  useEffect(() => {
    if (open) {
      track("lead_magnet_modal_opened", { magnet: "leak-checklist", source: "exit-intent" });
    }
  }, [open]);

  useEffect(() => {
    if (done && downloadUrl && downloadLinkRef.current) {
      downloadLinkRef.current.focus();
    }
  }, [done, downloadUrl]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    const result = validateLeadForm({ first_name: firstName, email, phone });
    if (!result.ok || !result.data) {
      setErrors(result.errors);
      setTouched({ first_name: true, email: true, phone: true });
      track("lead_magnet_form_validation_failed", {
        magnet: "leak-checklist",
        source: "exit-intent",
        fields: Object.keys(result.errors),
      });
      const first = Object.keys(result.errors)[0] as LeadFormField | undefined;
      if (first) {
        const id = first === "first_name" ? "exit-fn" : first === "email" ? "exit-em" : "exit-ph";
        (document.getElementById(id) as HTMLInputElement | null)?.focus();
      }
      toast.error("Please fix the highlighted fields");
      return;
    }
    const parsed = { data: result.data };
    setErrors({});
    track("lead_magnet_form_submitted", {
      magnet: "leak-checklist",
      source: "exit-intent",
      has_phone: Boolean(parsed.data.phone),
    });
    setSubmitting(true);
    try {
      const fnUrl = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/lead-magnet-capture`;
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          first_name: parsed.data.first_name,
          email: parsed.data.email,
          phone: parsed.data.phone || undefined,
          magnet: "leak-checklist",
          source_url: window.location.href,
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);

      const status = (res.headers.get("x-lead-status") as "ok" | "failed" | null) ?? "failed";
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);

      // Best-effort auto-download. The visible "Click here" link below is the
      // reliable path when the browser blocks programmatic downloads.
      let autoDownloadAttempted = false;
      try {
        const a = document.createElement("a");
        a.href = url;
        a.download = "new-patient-leak-checklist.pdf";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        autoDownloadAttempted = true;
      } catch {
        /* fallback link is shown regardless */
      }

      track("lead_magnet_pdf_download_started", {
        magnet: "leak-checklist",
        source: "exit-intent",
        trigger: "auto",
        auto_attempted: autoDownloadAttempted,
        lead_status: status,
        pdf_size_bytes: blob.size,
      });
      trackCompleteRegistration("lead-magnet-exit-intent", { extra: { magnet: "leak-checklist" } });
      track("exit_intent_lead_captured", { magnet: "leak-checklist", lead_status: status });
      setLeadStatus(status);
      setDone(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      track("lead_magnet_form_submit_failed", { magnet: "leak-checklist", source: "exit-intent", error: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        {done ? (
          <div className="space-y-4 text-center py-2">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
            <DialogTitle>
              {leadStatus === "ok" ? "Lead saved — your checklist is ready" : "Your checklist is ready"}
            </DialogTitle>
            <DialogDescription>
              If the download didn't start automatically, click the button below to grab the PDF.
            </DialogDescription>
            {downloadUrl && (
              <a
                ref={downloadLinkRef}
                href={downloadUrl}
                download="new-patient-leak-checklist.pdf"
                onClick={() => {
                  track("exit_intent_manual_download_click", { magnet: "leak-checklist" });
                  track("lead_magnet_pdf_download_started", {
                    magnet: "leak-checklist",
                    source: "exit-intent",
                    trigger: "manual",
                    lead_status: leadStatus,
                  });
                }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <FileDown className="h-4 w-4" />
                Click here to download the checklist
              </a>
            )}
            <p className="text-sm text-muted-foreground">
              Want a deeper read on your practice?{" "}
              <Link
                to="/quick-score"
                className="underline text-primary"
                onClick={() => {
                  track("lead_magnet_redirect_to_quick_score", {
                    magnet: "leak-checklist",
                    source: "exit-intent",
                    trigger: "post_download_link",
                    redirect_to: "/quick-score",
                  });
                  setOpen(false);
                }}
              >
                Try the 90-sec Score Quiz
              </Link>.
            </p>
            <Button variant="outline" onClick={() => setOpen(false)} className="mt-1">Close</Button>
          </div>
        ) : (
          <>

            <DialogHeader>
              <DialogTitle className="text-xl">Before you go — grab the New Patient Leak Checklist</DialogTitle>
              <DialogDescription>
                The 7-point self-audit we use on $1M+ practices. Spot the 2 biggest leaks in your front-desk → consult →
                close flow in under 10 minutes.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} noValidate className="space-y-3 mt-2">
              <div>
                <Label htmlFor="exit-fn">First name</Label>
                <Input
                  id="exit-fn"
                  value={firstName}
                  onChange={(e) => handleChange("first_name", e.target.value, setFirstName)}
                  onBlur={(e) => handleBlur("first_name", e.target.value)}
                  required
                  maxLength={80}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.first_name)}
                  aria-describedby={errors.first_name ? "exit-fn-err" : undefined}
                  className={errors.first_name ? "border-destructive focus-visible:ring-destructive" : undefined}
                />
                {errors.first_name && (
                  <p id="exit-fn-err" className="mt-1 flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3" /> {errors.first_name}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="exit-em">Email</Label>
                <Input
                  id="exit-em"
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => handleChange("email", e.target.value, setEmail)}
                  onBlur={(e) => handleBlur("email", e.target.value)}
                  required
                  maxLength={255}
                  autoComplete="email"
                  placeholder="you@practice.com"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "exit-em-err" : undefined}
                  className={errors.email ? "border-destructive focus-visible:ring-destructive" : undefined}
                />
                {errors.email && (
                  <p id="exit-em-err" className="mt-1 flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3" /> {errors.email}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="exit-ph">Phone <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  id="exit-ph"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => handleChange("phone", e.target.value, setPhone)}
                  onBlur={(e) => handleBlur("phone", e.target.value)}
                  maxLength={32}
                  autoComplete="tel"
                  placeholder="(555) 123-4567"
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? "exit-ph-err" : undefined}
                  className={errors.phone ? "border-destructive focus-visible:ring-destructive" : undefined}
                />
                {errors.phone && (
                  <p id="exit-ph-err" className="mt-1 flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3" /> {errors.phone}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={submitting || (attempted && !isValid)}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <FileDown className="h-4 w-4 mr-2" />
                Download the checklist
              </Button>
              <div className="text-center text-xs text-muted-foreground pt-1">
                Not ready?{" "}
                <Link
                  to="/quick-score"
                  className="underline hover:text-foreground inline-flex items-center gap-1"
                  onClick={() => {
                    track("exit_intent_downshift_quiz");
                    track("lead_magnet_redirect_to_quick_score", {
                      magnet: "leak-checklist",
                      source: "exit-intent",
                      trigger: "downshift_link",
                      redirect_to: "/quick-score",
                    });
                    setOpen(false);
                  }}
                >
                  Try the 90-sec Score Quiz <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
